/**
 * Redimensionnement des visuels, CÔTÉ NAVIGATEUR.
 *
 * ⭐ Extrait de PhotoCard pour être partagé avec la galerie d'articles : une
 * photo de téléphone pèse 3 à 5 Mo, et l'envoyer telle quelle traverserait un
 * réseau souvent instable — le sujet même du projet —, gonflerait la base et
 * surtout la réplique locale de CHAQUE appareil.
 */

/**
 * Résolution STOCKÉE des visuels.
 *
 * ⚠️ Arbitrage entre qualité et poids de la réplique, révisé.
 *
 * À 240×320, une photo agrandie dans le carrousel — qui s'affiche jusqu'à
 * 620 px de large — subissait un agrandissement de deux fois et demie. Le
 * résultat était visiblement flou : la fonction « regarder le visuel »
 * n'apportait alors rien de plus que la vignette.
 *
 * 640×854 borne le plus grand affichage possible du carrousel (720 px de
 * large, 78vh de haut). Stocker davantage serait du poids sans bénéfice
 * visible ; stocker moins, c'est réafficher du flou.
 *
 * ⚠️ Ce sont des MAXIMA, pas un format : l'image conserve ses proportions
 * et entre dans ce cadre. Un logo carré ressort en 640×640, une photo en
 * paysage en 640×480 — aucune n'est rognée.
 *
 * ⚠️ Ce n'est pas gratuit — un visuel passe d'environ 10 Ko à 70 Ko. Un
 * catalogue courant (cent articles, un ou deux visuels) occupe ~11 Mo, ce qui
 * reste très en deçà de la cible de 50 Mo (§7.5). En revanche le cas extrême
 * évoqué jusqu'ici — cinq visuels sur quatre cents articles — la dépasserait
 * largement. Ce jour-là, la réponse n'est PAS de réduire encore la
 * résolution : c'est de garder la vignette en local et de servir l'original
 * depuis le serveur à la demande, comme prévu pour les documents.
 */
export const PHOTO_WIDTH = 640
export const PHOTO_HEIGHT = 854 // borne verticale, pas un format imposé
// Relevée avec la résolution : à 0,72, les aplats d'un logo laissaient
// apparaître des halos de compression autour des lettres.
const QUALITY = 0.82
export const MAX_INPUT_BYTES = 15 * 1024 * 1024

/**
 * Formats d'entrée qui PEUVENT porter de la transparence — un JPEG, jamais.
 *
 * Sert à éviter le balayage des pixels quand il est certainement inutile,
 * c'est-à-dire dans le cas courant : une photo d'appareil.
 */
const TYPES_ALPHA = /^image\/(png|webp|gif|avif)$/

/** Un seul pixel non opaque suffit à trancher : on s'arrête au premier. */
function aDeLaTransparence(ctx: CanvasRenderingContext2D): boolean {
  const { data } = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height)
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] !== 255) return true
  }
  return false
}

/**
 * Encode le canevas, en CONSERVANT la transparence lorsqu'il y en a.
 *
 * ⚠️ C'est ici que se jouait un défaut livré : tout ressortait en JPEG, or le
 * JPEG n'a PAS de canal alpha. Un logo transparent était donc aplati sur du
 * NOIR au moment de l'enregistrement — pas à l'affichage. Le fond noir était
 * dès lors dans la donnée elle-même, et se retrouvait partout à la fois :
 * entête, sélecteur de boutique, carte de fidélité, documents imprimés.
 *
 * ⚠️ Le WebP plutôt que le PNG : il garde l'alpha pour une fraction du poids.
 * Un navigateur qui ne sait pas l'encoder rend du PNG de lui-même — ce qui
 * conserve l'alpha aussi, en plus lourd. Dans les deux cas la transparence
 * survit, et c'est la seule chose qui doit être garantie ici.
 *
 * ⭐ Le JPEG reste le choix par défaut : sur une photo — le gros du volume —
 * il pèse nettement moins que le WebP sans alpha à qualité comparable, et la
 * réplique locale de chaque appareil transporte ces images.
 */
export function encoderVisuel(ctx: CanvasRenderingContext2D, typeSource: string): string {
  const transparent = TYPES_ALPHA.test(typeSource) && aDeLaTransparence(ctx)
  return ctx.canvas.toDataURL(transparent ? 'image/webp' : 'image/jpeg', QUALITY)
}

/**
 * Réduit une image en CONSERVANT ses proportions.
 *
 * ⭐ Aucun recadrage : l'image entre dans un cadre de PHOTO_WIDTH × PHOTO_HEIGHT
 * sans qu'un seul pixel soit coupé. La version précédente rognait au centre
 * pour imposer un 3:4 — ce qui amputait un logo carré de ses bords et
 * décapitait une photo en paysage.
 *
 * ⚠️ Une image plus petite que le cadre n'est PAS agrandie : agrandir à
 * l'enregistrement fige un flou dans le fichier, alors que l'affichage peut
 * toujours étirer si besoin — et l'utilisateur voit alors que l'original était
 * petit, au lieu de croire à un défaut de l'application.
 */
export async function resizeToPortrait(
  file: File,
  maxWidth = PHOTO_WIDTH,
  maxHeight = PHOTO_HEIGHT,
): Promise<string> {
  const bitmap = await createImageBitmap(file)

  const facteur = Math.min(maxWidth / bitmap.width, maxHeight / bitmap.height, 1)
  const width = Math.max(1, Math.round(bitmap.width * facteur))
  const height = Math.max(1, Math.round(bitmap.height * facteur))

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Redimensionnement impossible sur cet appareil')

  ctx.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height, 0, 0, width, height)
  bitmap.close()

  return encoderVisuel(ctx, file.type)
}

/** Contrôles communs avant redimensionnement. Renvoie un message d'erreur ou `null`. */
export function refuserFichier(file: File): string | null {
  if (!file.type.startsWith('image/')) return 'Choisissez une image (JPEG, PNG ou WebP).'
  if (file.size > MAX_INPUT_BYTES) return 'Image trop lourde — 15 Mo maximum.'
  return null
}
